import {
  all,
  call,
  put,
  select,
  takeLatest,
  throttle,
} from 'redux-saga/effects';
import last from 'lodash/last';
import {
  cancelEditMessage,
  editMessage,
  editMessageSend,
  incrementMessageSent,
  selectRoom,
  sendMessage,
  sendMessageFailed,
  setEditMessage,
  setMessageSending,
  clearRoomJustOpened,
  toggleStartNewMessage,
  updateDrawerRoomLastMsg,
  deleteMessage,
  deleteMessageSuccess,
  deleteMessageFailure,
  quoteMessage,
  unsetRequestFlag,
} from '../../actions/room';
import { RealtimeChannel } from '@supabase/supabase-js';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { getRealtimeToken } from '../realtime';
import i18next from 'i18next';
import {
  getActiveRoomId,
  getActiveRoomItem,
  getIsMessageSending,
  getIsInRequests,
} from '../../selectors/room';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { setDrawerFilter } from '../../actions/drawer';
import {
  PapiDamzMessageDelete,
  type PapiDamzMessageReact,
  PapiDamzMessageSend,
  PapiDamzMessageUpdate,
  PapiDamzRoomCreateAndSend,
  PapiRequestDamzMessageDelete,
  type PapiRequestDamzMessageReact,
  PapiRequestDamzMessageSend,
  PapiRequestDamzMessageUpdate,
  PapiRequestDamzRoomCreateAndSend,
  PapiUser,
} from '@wix/da-papi-types';
import {
  findItemInStreams,
  getStream,
} from '@wix/da-shared-react/pkg/Stream/base/selectors';
import {
  isApiError,
  requestPuppy,
  XhrResponse,
  createFormData,
} from '@wix/da-http-client';
import { roomStreamGroupId, TEMP_DRAFT_ROOM_ID } from '../../../constants';
import {
  MessageItem,
  MessageAttachment,
  AttachmentType,
  ROOM_TYPE_TO_BI_STRING,
} from '../../../types';
import {
  createLastMsgFromSentMessage,
  getMessageCursor,
  getRoomStreamId,
} from '../../helpers';
import { getDraftMessage, getDraftParticipants } from '../../selectors/drafts';
import { addDraftAttachment, clearDraft } from '../../actions/drafts';
import { onActivity } from '../../actions/global';
import { attemptRejoinIfChannelsErrored } from './room-realtime';
import { getChatters, checkUserOnlineStateAndError } from '../helpers';
import {
  reactToMessage,
  reactToMessageSuccess,
  addCurrentUserToReaction,
  removeCurrentUserFromReaction,
  maybeEditLastMessage,
} from '../../actions/message';
import ddt from '@wix/da-ddt';
import {
  attachmentArrayToFormData,
  filterOutUploadProgressAttachments,
} from '../../../utils/attachments';
import { getCurrentUserHasReaction } from '../../selectors/reactionUsers';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import {
  BiData,
  MessageReactionAddClickBiEvent,
  MessageReactionRemoveClickBiEvent,
  MessageSendClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { markLastViewedMessage } from '../../actions/roomUnread';
import { MODAL_TYPES } from '../../../project-tools';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { getCanEditMessage } from '../../../utils/getCanEditMessage';
import { realtimeConnections } from '../../RealtimeConnections';

function* handleSendMessageFailed(
  action: ReturnType<typeof sendMessageFailed>
) {
  const currentUser: PapiUser = yield select(getCurrentUser);
  const { err, biData: payloadBiData = {} } = action.payload;
  if (!err) {
    return;
  }

  yield call(
    logBiEvent,
    BiData<MessageSendClickBiEvent>({
      evid: 474,
      uuid: currentUser.useridUuid,
      message_format: 'plain', // Our only supported format at present
      is_message_sent: 0,
      error_type: err.errorMessage,
      ...payloadBiData,
    })
  );
}

function* handleSendMessage(action: ReturnType<typeof sendMessage>) {
  let room_id = action.payload.isNewRoom
    ? TEMP_DRAFT_ROOM_ID
    : yield select(getActiveRoomId);

  const isSending = yield select(state => getIsMessageSending(state, room_id));
  if (isSending) {
    return;
  }

  // Check if user is offline before sending
  const isOnline = yield call(checkUserOnlineStateAndError);
  if (!isOnline) {
    return;
  }

  const currentUser: PapiUser = yield select(getCurrentUser);
  const draftMessage:
    | { ts: number; text: string; attachments: MessageAttachment[] }
    | undefined = yield select(state => getDraftMessage(state, room_id));
  const draftUsers: PapiUser[] = yield select(state =>
    getDraftParticipants(state)
  );
  const quoteAttachment = (draftMessage?.attachments || []).find(att =>
    Object.prototype.hasOwnProperty.call(att, 'messageId')
  );

  if (
    !draftMessage ||
    (!draftMessage.text && draftMessage.attachments.length === 0)
  ) {
    yield put(
      sendMessageFailed(
        {
          errorDescription: 'No message content to send',
          errorKey: 'damz.error.msg.send.failed.no.content',
        },
        {
          room_id,
          room_type: draftUsers.length > 1 ? 'multi' : 'dm',
          is_edit: 0,
          is_reply: quoteAttachment ? 1 : 0,
        }
      )
    );
    return;
  }

  const messageText = draftMessage.text ?? '';

  const messageAttachments = filterOutUploadProgressAttachments(
    draftMessage.attachments ?? []
  );

  yield put(setMessageSending(room_id, true));

  // Ensure we have a fresh CSRF token before sending
  const sendToken = yield call(getRealtimeToken);
  if (!sendToken) {
    yield put(setMessageSending(room_id, false));
    if (Math.floor(Math.random() * 100) === 0) {
      ddt.alert(
        'damz',
        'Failed to get CSRF token, trying to reconnect to the realtime channel',
        ['damz', 'csrf'],
        {
          room_id,
        }
      );
    }
    yield realtimeConnections.reconnect();
    yield put(
      sendMessageFailed(
        {
          errorDescription: 'Failed to get CSRF token',
          errorKey: 'damz.error.msg.send.failed.notoken',
        },
        {
          room_type: draftUsers.length > 1 ? 'multi' : 'dm',
          is_edit: 0,
          is_reply: quoteAttachment ? 1 : 0,
        }
      )
    );
    return;
  }

  try {
    if (room_id === TEMP_DRAFT_ROOM_ID) {
      if (!draftUsers || draftUsers.length === 0) {
        yield put(
          sendMessageFailed(
            {
              errorDescription:
                'You must select at least one user to send a message',
            },
            {
              room_type: draftUsers.length > 1 ? 'multi' : 'dm',
              is_edit: 0,
              is_reply: quoteAttachment ? 1 : 0,
            }
          )
        );
        return;
      }
    }

    // Make sure realtime is working before sending to stop dupes messages
    const channels: RealtimeChannel[] = yield call(
      attemptRejoinIfChannelsErrored
    );
    const allJoined = channels.every(
      // accept joining as well as the send will work
      ch => ch.state === 'joined' || ch.state === 'joining'
    );
    if (!allJoined) {
      ddt.alert(
        'damz',
        'Realtime channel failed to rejoin',
        ['damz', 'realtime'],
        {
          room_id,
          channels: channels.map(ch => ({
            room: ch.topic,
            state: ch.state,
          })),
        }
      );
      yield put(
        sendMessageFailed(
          {
            errorDescription: 'There was an error sending the message',
            errorKey: 'damz.realtime.send.error',
          },
          {
            room_type: draftUsers.length > 1 ? 'multi' : 'dm',
            is_edit: 0,
            is_reply: quoteAttachment ? 1 : 0,
          }
        )
      );
      yield put(
        addErrorNotification(i18next.t('damz:damz.realtime.send.error'))
      );
      return;
    }

    if (messageAttachments.length === 0 && messageText === '') {
      yield put(
        sendMessageFailed(
          {
            errorDescription: 'No message content to send',
            errorKey: 'damz.error.msg.send.failed.no.content',
          },
          {
            room_type: draftUsers.length > 1 ? 'multi' : 'dm',
            is_edit: 0,
            is_reply: quoteAttachment ? 1 : 0,
          }
        )
      );
      return;
    }

    const allChatters = yield call(getChatters, room_id);
    let response:
      | XhrResponse<PapiDamzMessageSend>
      | XhrResponse<PapiDamzRoomCreateAndSend>
      | undefined;

    if (room_id === TEMP_DRAFT_ROOM_ID) {
      const formData = createFormData<PapiRequestDamzRoomCreateAndSend>({
        send_token: sendToken,
        msg: messageText,
        type: draftUsers.length > 1 ? 'multi' : 'dm',
        participant_ids: draftUsers.map(user => user.useridUuid),
        attachments:
          messageAttachments.length > 0
            ? messageAttachments.reduce((acc, attachment) => {
                for (const slot of ['a', 'b', 'c', 'd', 'e', 'f']) {
                  if (!acc[slot]) {
                    acc[slot] = attachment;
                    return acc;
                  }
                }
                return acc;
              }, {})
            : undefined,
      });

      response = yield call(
        requestPuppy,
        {
          method: 'post',
          url: 'room/create_and_send',
          data: formData,
        },
        undefined,
        'damz'
      );
    } else {
      const formData = createFormData<PapiRequestDamzMessageSend>({
        room_id,
        send_token: sendToken,
        msg: messageText,
        chatters: allChatters,
        attachments:
          messageAttachments.length > 0
            ? messageAttachments.reduce((acc, attachment) => {
                for (const slot of ['a', 'b', 'c', 'd', 'e', 'f']) {
                  if (!acc[slot]) {
                    acc[slot] = attachment;
                    return acc;
                  }
                }
                return acc;
              }, {})
            : undefined,
      });

      response = yield call(
        requestPuppy,
        {
          method: 'post',
          url: 'message/send',
          data: formData,
        },
        undefined,
        'damz'
      );
    }

    if (!response || isApiError(response)) {
      if (response === false) {
        yield put(
          sendMessageFailed(
            {
              errorDescription: 'Failed to send message',
              errorKey: 'damz.error.msg.send.failed.generic',
            },
            {
              room_id,
              room_type: draftUsers.length > 1 ? 'multi' : 'dm',
              is_edit: 0,
              is_reply: quoteAttachment ? 1 : 0,
            }
          )
        );
      } else {
        yield put(
          sendMessageFailed(
            {
              errorDescription: response?.errorDescription,
              errorKey: response?.errorDetails?.errorKey,
            },
            {
              room_id,
              room_type: draftUsers.length > 1 ? 'multi' : 'dm',
              is_edit: 0,
              is_reply: quoteAttachment ? 1 : 0,
            }
          )
        );
      }
      return;
    }

    // Map the new room_id
    if (room_id === TEMP_DRAFT_ROOM_ID) {
      room_id = response.message?.roomId;
    }

    // If the room was a request, switch to the all filter, and unset the flag
    const isInRequests = yield select(getIsInRequests);
    if (isInRequests) {
      yield put(setDrawerFilter('all'));
      yield put(unsetRequestFlag(room_id));
    }

    yield put(clearDraft(room_id));
    yield put(incrementMessageSent());
    yield put(sendMessageFailed(undefined, {}));
    yield put(setMessageSending(room_id, false));

    // Clear just opened state when user sends a message
    yield put(clearRoomJustOpened(room_id));

    const lastMsg = createLastMsgFromSentMessage(
      messageText,
      currentUser,
      response.message?.messageId || '',
      undefined
    );
    yield put(updateDrawerRoomLastMsg(room_id, lastMsg));

    if (action.payload.isNewRoom) {
      yield put(clearDraft(TEMP_DRAFT_ROOM_ID));
      yield put(setMessageSending(TEMP_DRAFT_ROOM_ID, false));
      yield put(onActivity(room_id));
      // Select the new room
      yield put(selectRoom(room_id, undefined, false));
      // Close new message block
      yield put(toggleStartNewMessage());
    }

    // Mark last seen, if you send a message assume its now head for you
    if (response.message) {
      yield put(markLastViewedMessage(room_id, response.message.messageId));

      yield call(
        logBiEvent,
        BiData<MessageSendClickBiEvent>({
          evid: 474,
          message_id: response.message.messageId,
          room_id,
          room_type: draftUsers.length > 1 ? 'multi' : 'dm',
          uuid: currentUser.useridUuid,
          message_format: 'plain', // No HTML support at this time
          is_message_sent: 1,
          is_edit: 0,
          is_reply: quoteAttachment ? 1 : 0,
        })
      );
    }
  } finally {
    // Reset sending state for the room that was actually being used for sending
    // If room_id was reassigned during room creation, use the final room_id
    // Otherwise, use the original room_id
    const roomToReset =
      room_id ||
      (action.payload.isNewRoom
        ? TEMP_DRAFT_ROOM_ID
        : yield select(getActiveRoomId));
    yield put(setMessageSending(roomToReset, false));
  }
}

function* handleEditMessage(action: ReturnType<typeof editMessage>) {
  const activeRoom = yield select(getActiveRoomId) ?? '';
  const msg = yield select(state =>
    findItemInStreams(state, roomStreamGroupId, (item: MessageItem) => {
      return item.msg.message_id === action.payload.messageId;
    })
  );

  if (!msg) {
    yield put(
      addErrorNotification(i18next.t('damz:damz.room.messages.edit.not_found'))
    );
    return;
  }

  yield put(setEditMessage(activeRoom, msg));
}

function* handleEditMessageSend(action: ReturnType<typeof editMessageSend>) {
  const { updatedText = '', updatedAttachments } = action.payload;
  const currentUser: PapiUser = yield select(getCurrentUser);
  const activeRoomId = yield select(getActiveRoomId);
  const activeRoomItem = yield select(getActiveRoomItem);
  const possibleEditDraft: ReturnType<typeof getDraftMessage> = yield select(
    getDraftMessage,
    activeRoomId
  );
  const roomType = ROOM_TYPE_TO_BI_STRING[activeRoomItem?.room?.room_type];
  const quoteAttachment = updatedAttachments.find(att =>
    Object.prototype.hasOwnProperty.call(att, 'messageId')
  );

  // Check if user is offline before editing
  const isOnline = yield call(checkUserOnlineStateAndError);
  if (!isOnline) {
    return;
  }

  if (!possibleEditDraft?.editMessageId || !activeRoomId) {
    yield put(
      sendMessageFailed(
        {
          errorDescription: 'No message to edit',
          errorKey: 'damz.error.msg.edit.failed.no.message',
        },
        {
          room_id: activeRoomId,
          room_type: roomType,
          is_edit: 1,
          is_reply: quoteAttachment ? 1 : 0,
        }
      )
    );
    return;
  }

  const isSending = yield select(state =>
    getIsMessageSending(state, activeRoomId)
  );
  if (isSending) {
    return;
  }

  yield put(setMessageSending(activeRoomId, true));

  // Ensure we have a fresh CSRF token before sending
  const sendToken = yield call(getRealtimeToken);
  if (!sendToken) {
    yield put(
      sendMessageFailed(
        {
          errorDescription: 'Failed to get CSRF token',
          errorKey: 'damz.error.msg.send.failed.notoken',
        },
        {
          room_id: activeRoomId,
          room_type: roomType,
          is_edit: 1,
          is_reply: quoteAttachment ? 1 : 0,
        }
      )
    );
    return;
  }

  try {
    const allChatters = yield call(getChatters, activeRoomId);
    const formData = createFormData<PapiRequestDamzMessageUpdate>({
      message_id: possibleEditDraft.editMessageId,
      msg: updatedText,
      attachments: attachmentArrayToFormData(updatedAttachments),
      chatters: allChatters,
      send_token: sendToken,
    });

    const response: XhrResponse<PapiDamzMessageUpdate> = yield call(
      requestPuppy,
      {
        method: 'post',
        url: 'message/update',
        data: formData,
      },
      undefined,
      'damz'
    );

    if (!response || isApiError(response)) {
      if (response === false) {
        yield put(
          sendMessageFailed(
            {
              errorDescription: 'Failed to send message',
              errorKey: 'damz.error.msg.send.failed.generic',
            },
            {
              room_id: activeRoomId,
              room_type: roomType,
              is_edit: 1,
              is_reply: quoteAttachment ? 1 : 0,
            }
          )
        );
      } else {
        yield put(
          sendMessageFailed(
            {
              errorDescription: response.errorDescription,
              errorKey: response?.errorDetails?.errorKey,
            },
            {
              room_id: activeRoomId,
              room_type: roomType,
              is_edit: 1,
              is_reply: quoteAttachment ? 1 : 0,
            }
          )
        );
      }
      return;
    }

    yield put(cancelEditMessage(activeRoomId));
    yield put(incrementMessageSent());
    yield put(sendMessageFailed(undefined, {}));

    const message = response.message;
    if (message) {
      const stream: ReturnType<typeof getStream<MessageItem>> = yield select(
        state => getStream(state, getRoomStreamId(message.roomId))
      );

      if (last(stream.items)?.msg.message_id === message.messageId) {
        const currentUserForMsg: PapiUser = yield select(getCurrentUser);
        const lastMsg = createLastMsgFromSentMessage(
          message.msg ?? '',
          currentUserForMsg,
          message.messageId,
          undefined
        );
        yield put(updateDrawerRoomLastMsg(activeRoomId, lastMsg));
      }

      yield call(
        logBiEvent,
        BiData<MessageSendClickBiEvent>({
          evid: 474,
          message_id: message.messageId,
          room_id: activeRoomId,
          room_type: roomType,
          uuid: currentUser.useridUuid,
          message_format: 'plain', // No HTML support at this time
          is_message_sent: 1,
          is_edit: 1,
          is_reply: quoteAttachment ? 1 : 0,
        })
      );
    }
  } finally {
    yield put(setMessageSending(activeRoomId, false));
  }
}

function* handleReactToMessage(action: ReturnType<typeof reactToMessage>) {
  const { messageId, emoji_id, sectionname } = action.payload;
  const activeRoom = yield select(getActiveRoomId);
  const activeRoomItem = yield select(getActiveRoomItem);
  const chatters = yield call(getChatters, activeRoom);
  const currentUser: PapiUser = yield select(getCurrentUser);

  const isOnline = yield call(checkUserOnlineStateAndError);
  if (!isOnline) {
    return;
  }

  // Limit the number of reactions to 25
  const message: MessageItem = yield select(state =>
    findItemInStreams(state, roomStreamGroupId, (item: MessageItem) => {
      return item.msg.message_id === messageId;
    })
  );
  if (!message) {
    yield putErrorNotification(
      i18next.t('damz:damz.error.msg.react.message.not.found')
    );
    return;
  }

  // Check if user already has this reaction to determine if adding or removing
  const userHasReaction = yield select(
    getCurrentUserHasReaction,
    messageId,
    emoji_id,
    currentUser
  );

  const formData = createFormData<PapiRequestDamzMessageReact>({
    message_id: messageId,
    emoji_id: `${emoji_id}`,
    chatters,
  });
  let result: PapiDamzMessageReact;
  try {
    result = yield call(
      requestPuppy,
      {
        method: 'post',
        url: 'message/react',
        data: formData,
      },
      undefined,
      'damz'
    );
  } catch (err) {
    ddt.error('damz', 'handleReactToMessage failed', err);
    yield putErrorNotification(i18next.t('damz:damz.error.msg.react.failed'));
    return;
  }

  if (isApiError(result) || !result) {
    if (result.errorDetails?.errorKey === 'damz.error.reaction.bucket.limit') {
      yield put(pushModal(MODAL_TYPES.REACTION_LIMIT_MODAL, {}));
      return;
    }
    yield putErrorNotification(i18next.t('damz:damz.error.msg.react.failed'));
    return;
  }

  yield put(reactToMessageSuccess(messageId, emoji_id));

  // Get common data for BI logging - now using emoji_id
  const roomType = ROOM_TYPE_TO_BI_STRING[activeRoomItem?.room?.room_type];

  // Update the reaction users list and log BI event based on whether user is adding or removing reaction
  if (userHasReaction) {
    // User is removing their reaction
    yield put(removeCurrentUserFromReaction(messageId, emoji_id, currentUser));

    yield call(
      logBiEvent,
      BiData<MessageReactionRemoveClickBiEvent>({
        evid: 476,
        message_id: messageId,
        room_id: activeRoom,
        uuid: currentUser.useridUuid,
        room_type: roomType,
        reaction_type_id: emoji_id,
        sectionname,
      })
    );
  } else {
    // User is adding a reaction
    yield put(addCurrentUserToReaction(messageId, emoji_id, currentUser));

    yield call(
      logBiEvent,
      BiData<MessageReactionAddClickBiEvent>({
        evid: 475,
        message_id: messageId,
        room_id: activeRoom,
        uuid: currentUser.useridUuid,
        room_type: roomType,
        reaction_type_id: emoji_id,
        sectionname,
      })
    );
  }
}

function* handleDeleteMessage(action: ReturnType<typeof deleteMessage>) {
  const { messageId } = action.payload;
  const activeRoom = yield select(getActiveRoomId);
  const chatters = yield call(getChatters, activeRoom);

  const isOnline = yield call(checkUserOnlineStateAndError);
  if (!isOnline) {
    return;
  }

  const formData = createFormData<PapiRequestDamzMessageDelete>({
    message_id: messageId,
    chatters,
  });

  let result: PapiDamzMessageDelete;
  try {
    result = yield call(
      requestPuppy,
      {
        method: 'post',
        url: 'message/delete',
        data: formData,
      },
      undefined,
      'damz'
    );
  } catch (err) {
    ddt.error('damz', 'handleDeleteMessage failed', err);
    yield put(deleteMessageFailure(messageId, 'Failed to delete message'));
    return;
  }

  if (isApiError(result) || !result) {
    yield put(deleteMessageFailure(messageId, result.errorDescription));
    return;
  }

  yield put(deleteMessageSuccess(messageId));
}

function* handleQuoteMessage(action: ReturnType<typeof quoteMessage>) {
  const { messageId } = action.payload;
  const message: MessageItem = yield select(state =>
    findItemInStreams(state, roomStreamGroupId, (item: MessageItem) => {
      return item.msg.message_id === messageId;
    })
  );

  if (!message || !message.user) {
    ddt.error('damz', 'Message data malformed for quote attachment', {
      message,
    });
    return;
  }
  const activeRoom = yield select(getActiveRoomId);
  yield put(
    addDraftAttachment(
      {
        attachmentType: AttachmentType.Quote,
        messageId: message.msg.message_id,
        link: getMessageCursor(message.msg.message_id, message.msg.sent_at),
        msg: message.msg.msg,
        user: message.user,
      },
      activeRoom
    )
  );
}

function* handleMaybeEditLastMessage(
  action: ReturnType<typeof maybeEditLastMessage>
) {
  const activeRoom = yield select(getActiveRoomId);
  const currentUser: PapiUser = yield select(getCurrentUser);

  if (!activeRoom || !currentUser) {
    return;
  }

  // Get the message stream for the active room
  const stream: ReturnType<typeof getStream<MessageItem>> = yield select(
    state => getStream(state, getRoomStreamId(activeRoom))
  );

  if (!stream?.items || stream.items.length === 0) {
    return;
  }

  for (let i = stream.items.length - 1; i >= 0; i--) {
    const item = stream.items[i];

    if (
      !item.msg.deleted_at &&
      getCanEditMessage(
        item.msg.sent_at,
        item.user?.useridUuid === currentUser.useridUuid
      )
    ) {
      yield put(editMessage(item.msg.message_id));
      break;
    }
  }
}

/**
 * Message actions saga - handles sending and editing messages
 */
export function* roomMessageActionsSaga() {
  yield all([
    throttle(1000, sendMessage, handleSendMessage),
    takeLatest(editMessage, handleEditMessage),
    takeLatest(maybeEditLastMessage, handleMaybeEditLastMessage),
    throttle(1000, editMessageSend, handleEditMessageSend),
    takeLatest(reactToMessage, handleReactToMessage),
    takeLatest(deleteMessage, handleDeleteMessage),
    takeLatest(quoteMessage, handleQuoteMessage),
    takeLatest(sendMessageFailed, handleSendMessageFailed),
  ]);
}
